import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToastWithSound } from '../hooks/useToastWithSound';
import { addSale, getMySales, getAllSales } from '../store/saleSlice';
import { getServices } from '../store/serviceSlice';
import { logout } from '../store/authSlice';
import { getDashboardStats, getBranchStats, getRecentActivities } from '../store/statsSlice';
import Sidebar from '../components/Sidebar';
import ModernHeader from '../components/ModernHeader';
import DashboardStats from '../components/DashboardStats';
import DashboardOverview from '../components/DashboardOverview';
import SalesReports from '../components/SalesReports';

const StaffDashboardScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [serviceId, setServiceId] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [saleAmount, setSaleAmount] = useState('');
  const [profit, setProfit] = useState('');
  const [showSalePopup, setShowSalePopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showActionToast, showError } = useToastWithSound();

  // Function to refresh dashboard data
  const refreshDashboardData = () => {
    dispatch(getDashboardStats());
    dispatch(getBranchStats());
    dispatch(getRecentActivities());
  };

  // Handle tab change and refresh data when switching to overview
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab }); // Update URL to maintain state on reload
    if (newTab === 'overview') {
      refreshDashboardData();
    } else if (newTab === 'sales') {
      dispatch(getAllSales({ page: currentPage, limit: recordsPerPage }));
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(getAllSales({ page, limit: recordsPerPage }));
  };

  // Handle records per page change
  const handleRecordsPerPageChange = (limit) => {
    setRecordsPerPage(limit);
    setCurrentPage(1);
    dispatch(getAllSales({ page: 1, limit }));
  };

  const { userInfo } = useSelector((state) => state.auth);
  const { services, loading: servicesLoading, error: servicesError } = useSelector((state) => state.services);
  const { sales, allSales, pagination, loading: salesLoading, allSalesLoading, error: salesError } = useSelector((state) => state.sales);

  useEffect(() => {
    // Check if user is authenticated
    if (!userInfo || !userInfo.token) {
      navigate('/login');
      return;
    }
    
    if (userInfo.role === 'Admin') {
      navigate('/admin-dashboard');
      return;
    }

    // Check token expiration
    checkTokenExpiration();

    if (userInfo) {
        dispatch(getServices());
        dispatch(getMySales());
        // Load all sales for sales management
        if (activeTab === 'sales') {
          dispatch(getAllSales({ page: currentPage, limit: recordsPerPage }));
        }
        // Load dashboard data on initial load
        if (activeTab === 'overview') {
          refreshDashboardData();
        }
    }
  }, [dispatch, userInfo, activeTab, currentPage, recordsPerPage]);

  // Click outside handler for service dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.service-dropdown-container')) {
        setShowServiceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const checkTokenExpiration = () => {
    if (userInfo && userInfo.token) {
      try {
        const tokenPayload = JSON.parse(atob(userInfo.token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (tokenPayload.exp < currentTime) {
          handleLogout();
        }
      } catch (error) {
        console.error('Token validation error:', error);
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  useEffect(() => {
      if (servicesError) showError(servicesError);
      if (salesError) showError(salesError);
  }, [servicesError, salesError, showError])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.service-dropdown-container')) {
        setShowServiceDropdown(false);
      }
    };

    if (showServiceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showServiceDropdown]);

  // Filter services based on search
  const filteredServices = services.filter(service => 
    service.name.toLowerCase().includes(serviceSearch.toLowerCase())
  );

  const handleServiceSelect = (service) => {
    setServiceId(service._id);
    setServiceName(service.name);
    setServiceSearch(service.name);
    setShowServiceDropdown(false);
  };

  const submitHandler = (e) => {
    e.preventDefault();
    if (!serviceId || !saleAmount || !profit) {
      showActionToast('validationError', false);
      return;
    }
    const branch = localStorage.getItem('branch');
    const saleData = { 
      customerName: 'Walk-in Customer', // Default customer name
      customerMobile: '0000000000', // Default mobile
      serviceId, 
      price: parseFloat(saleAmount), 
      cost: parseFloat(saleAmount) - parseFloat(profit), // Calculate cost from sale amount and profit
      branch 
    };
    dispatch(addSale(saleData)).then(() => {
        showActionToast('addSale', true);
        // Reset form
        setServiceId('');
        setServiceName('');
        setServiceSearch('');
        setSaleAmount('');
        setProfit('');
        setShowSalePopup(false);
        // Refresh sales list and dashboard data
        dispatch(getMySales());
        dispatch(getAllSales({ page: currentPage, limit: recordsPerPage }));
        refreshDashboardData();
    }).catch(() => {
        showActionToast('addSale', false);
    });
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            {/* Welcome Message */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white p-8 rounded-2xl shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold mb-2">Welcome back, {userInfo?.name}! 👋</h1>
                  <p className="text-blue-100 text-lg">Ready to serve customers and make sales today</p>
                  <p className="text-blue-200 text-sm mt-2">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
                <div className="hidden md:block">
                  <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">💼</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Dashboard Stats */}
            <DashboardStats userRole={userInfo?.role || 'Staff'} />
            
            {/* Dashboard Overview */}
            <DashboardOverview userRole={userInfo?.role || 'Staff'} />
          </div>
        );
      case 'sales':
        return (
          <div className="space-y-8">
            {/* Sales Management Header */}
            <div className="bg-gradient-to-r from-green-600 to-blue-700 text-white p-6 rounded-2xl shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold mb-2">Sales Management 🛒</h1>
                  <p className="text-green-100">Add new sales and manage all transactions</p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowSalePopup(true)}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg transform hover:scale-105 backdrop-blur-sm border border-white border-opacity-30"
                  >
                    + Add New Sale
                  </button>
                  <div className="hidden md:block">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <span className="text-2xl">💰</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales Table */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">All Sales Records</h3>
                    <p className="text-gray-600 mt-1">Manage and view all sales transactions</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Show:</span>
                      <select
                        value={recordsPerPage}
                        onChange={(e) => handleRecordsPerPageChange(parseInt(e.target.value))}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-sm text-gray-600">entries</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table Content */}
              <div className="overflow-x-auto">
                {allSalesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center space-x-3">
                      <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-xl text-gray-600">Loading sales...</span>
                    </div>
                  </div>
                ) : allSales?.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <span className="text-6xl">📊</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Sales Found</h3>
                    <p className="text-gray-500">Start by adding your first sale</p>
                  </div>
                ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale Amount</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profit</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allSalesLoading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center">
                          <div className="flex items-center justify-center">
                            <svg className="animate-spin h-6 w-6 text-blue-500 mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading sales...
                          </div>
                        </td>
                      </tr>
                    ) : allSales && allSales.length > 0 ? (
                      allSales.map((sale) => (
                        <tr key={sale._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
                                <span className="text-white text-xs font-bold">{sale.service?.name?.charAt(0) || 'S'}</span>
                              </div>
                              <span className="font-medium text-gray-900">{sale.service?.name || 'Unknown Service'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                              ₹{sale.price?.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                              ₹{sale.profit?.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center mr-3">
                                <span className="text-white text-xs font-bold">{sale.user?.name?.charAt(0) || 'U'}</span>
                              </div>
                              <span className="font-medium text-gray-900">{sale.user?.name || 'Unknown User'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {sale.branch}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              <div className="font-medium">{new Date(sale.createdAt).toLocaleDateString('en-IN')}</div>
                              <div className="text-sm text-gray-500">{new Date(sale.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center">
                          <div className="text-gray-500">
                            <span className="text-4xl mb-4 block">📊</span>
                            <p className="text-lg font-medium">No sales records found</p>
                            <p className="text-sm">Add your first sale to get started</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    </tbody>
                  </table>
                )}
              </div>
              
              {/* Pagination */}
              {allSales && allSales.length > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.currentPage - 1) * pagination.recordsPerPage) + 1} to {Math.min(pagination.currentPage * pagination.recordsPerPage, pagination.totalRecords)} of {pagination.totalRecords} entries
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrevPage}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {/* Page Numbers */}
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 border rounded-lg text-sm font-medium ${
                              pageNum === pagination.currentPage
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNextPage}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sale Entry Popup */}
            {showSalePopup && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-white text-xl">💰</span>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800">Add New Sale</h2>
                      </div>
                      <button
                        onClick={() => setShowSalePopup(false)}
                        className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                      >
                        ×
                      </button>
                    </div>
                    
                    <form onSubmit={submitHandler} className="space-y-4">
                      {/* Service Name - Searchable */}
                      <div className="space-y-2 relative service-dropdown-container">
                        <label className="text-sm font-semibold text-gray-700">Service Name *</label>
                        <input
                          type="text"
                          placeholder="Type to search services..."
                          value={serviceSearch}
                          onChange={(e) => {
                            setServiceSearch(e.target.value);
                            setShowServiceDropdown(true);
                            if (!e.target.value) {
                              setServiceId('');
                              setServiceName('');
                            }
                          }}
                          onFocus={() => setShowServiceDropdown(true)}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                          required
                        />
                        
                        {/* Dropdown for services */}
                        {showServiceDropdown && serviceSearch && (
                          <div className="absolute z-10 w-full bg-white border-2 border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg">
                            {servicesLoading ? (
                              <div className="p-3 text-center text-gray-500 text-sm">Loading services...</div>
                            ) : filteredServices.length > 0 ? (
                              filteredServices.map((service) => (
                                <div
                                  key={service._id}
                                  onClick={() => handleServiceSelect(service)}
                                  className="p-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-gray-800 text-sm">{service.name}</div>
                                  {service.description && (
                                    <div className="text-xs text-gray-500 mt-1">{service.description}</div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="p-3 text-center text-gray-500 text-sm">No services found</div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Sale Amount */}
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Sale Amount (₹) *</label>
                        <input
                          type="number"
                          placeholder="Enter sale amount"
                          value={saleAmount}
                          onChange={(e) => setSaleAmount(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      
                      {/* Profit */}
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Profit (₹) *</label>
                        <input
                          type="number"
                          placeholder="Enter profit amount"
                          value={profit}
                          onChange={(e) => setProfit(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      
                      {/* Submit Buttons */}
                      <div className="flex space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowSalePopup(false)}
                          className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="flex-1 bg-gradient-to-r from-green-500 to-blue-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-green-600 hover:to-blue-700 transition-all duration-200"
                          disabled={salesLoading}
                        >
                          {salesLoading ? (
                            <div className="flex items-center justify-center">
                              <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Adding...
                            </div>
                          ) : (
                            'Add Sale'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'reports':
        return <SalesReports userRole={userInfo?.role || 'Staff'} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={handleTabChange} 
        userRole={userInfo?.role || 'Staff'} 
      />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col ml-64">
        {/* Header */}
        <ModernHeader user={userInfo} onLogout={handleLogout} isStaffPanel={true} />
        
        {/* Dashboard Content */}
        <main className="flex-1 overflow-y-auto p-8">
          
          {/* Tab Content */}
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default StaffDashboardScreen;

